import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Box,
  makeStyles,
  Chip,
  Divider,
  Card,
  CardContent,
} from "@material-ui/core";
import {
  Save,
  CloudUpload,
  DeleteOutline,
  CheckCircle,
  Email,
  AccessTime,
  Palette,
  PaymentOutlined,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 960,
    margin: "0 auto",
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  pageHeader: {
    marginBottom: theme.spacing(3),
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: "1.6rem",
    color: theme.palette.text.primary,
  },
  pageSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    marginTop: 4,
  },
  tabBar: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
    overflowX: "auto",
    paddingBottom: 4,
    "&::-webkit-scrollbar": { height: 4 },
    "&::-webkit-scrollbar-thumb": { background: "#ddd", borderRadius: 2 },
  },
  tabBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 18px",
    borderRadius: 10,
    border: "1.5px solid transparent",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: "0.85rem",
    whiteSpace: "nowrap",
    transition: "all 0.2s",
    background: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
    "&:hover": {
      background: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },
  tabBtnActive: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: "#fff",
    border: "1.5px solid transparent",
    boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
    "&:hover": {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
      color: "#fff",
    },
  },
  tabIcon: {
    width: 20,
    height: 20,
    objectFit: "contain",
    borderRadius: 4,
  },
  panel: {
    background: theme.palette.background.paper,
    borderRadius: 12,
    padding: theme.spacing(3),
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    border: `1px solid ${theme.palette.divider}`,
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  panelLogoBox: {
    width: 52,
    height: 52,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  panelTitle: {
    fontWeight: 600,
    fontSize: "1.1rem",
  },
  panelDescription: {
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
    marginTop: 2,
  },
  infoCard: {
    background: theme.palette.action.hover,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  infoCardText: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  textField: {
    marginBottom: theme.spacing(0.5),
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
    marginTop: 4,
    marginBottom: theme.spacing(2),
    lineHeight: 1.5,
  },
  actions: {
    marginTop: theme.spacing(3),
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  saveButton: {
    minWidth: 160,
    borderRadius: 8,
    fontWeight: 600,
    padding: "8px 24px",
    textTransform: "none",
    fontSize: "0.95rem",
  },
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
  brandingPreviewImg: {
    maxWidth: "100%",
    maxHeight: 100,
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    objectFit: "contain",
    background: "#f5f5f5",
    padding: 4,
  },
  uploadButton: {
    marginRight: theme.spacing(1),
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.82rem",
  },
  uploadActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  statusChip: {
    fontSize: "0.75rem",
    height: 24,
    borderRadius: 6,
  },
  webhookBox: {
    background: theme.palette.action.selected,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    fontFamily: "monospace",
    fontSize: "0.82rem",
    color: theme.palette.text.primary,
    wordBreak: "break-all",
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
}));

// Logo Mercado Pago SVG inline
const MercadoPagoLogo = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <rect width="48" height="48" rx="10" fill="#00BCFF"/>
    <path d="M24 8C15.16 8 8 15.16 8 24C8 32.84 15.16 40 24 40C32.84 40 40 32.84 40 24C40 15.16 32.84 8 24 8Z" fill="white"/>
    <path d="M16 24L20.5 18L25 22.5L29.5 17L34 24L29.5 30L25 25.5L20.5 29.5L16 24Z" fill="#00BCFF"/>
  </svg>
);

// Logo Asaas SVG inline
const AsaasLogo = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <rect width="48" height="48" rx="10" fill="#0066CC"/>
    <path d="M24 10C24 10 14 18 14 26C14 31.52 18.48 36 24 36C29.52 36 34 31.52 34 26C34 18 24 10 24 10Z" fill="white" opacity="0.9"/>
    <path d="M24 16C24 16 18 22 18 27C18 30.31 20.69 33 24 33C27.31 33 30 30.31 30 27C30 22 24 16 24 16Z" fill="#0066CC"/>
    <circle cx="24" cy="27" r="3" fill="white"/>
  </svg>
);

const resolveImageUrl = (value) => {
  if (!value) return "";
  if (value.startsWith("http")) return value;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (!base) return value;
  const normalizedBase = base.replace(/\/+$/, "");
  const path = value.startsWith("/") ? value : "/" + value;
  return normalizedBase + path;
};

const TABS = [
  {
    id: 0,
    label: "Mercado Pago",
    icon: "mp",
    color: "#00BCFF",
  },
  {
    id: 1,
    label: "Asaas",
    icon: "asaas",
    color: "#0066CC",
  },
  {
    id: 2,
    label: "E-mail (SMTP)",
    icon: "email",
    color: "#EA4335",
  },
  {
    id: 3,
    label: "Trial / Assinatura",
    icon: "trial",
    color: "#FF8C00",
  },
  {
    id: 4,
    label: "Login / Capa",
    icon: "login",
    color: "#9C27B0",
  },
];

const TabIconSvg = ({ icon, active }) => {
  if (icon === "mp") return <MercadoPagoLogo size={20} />;
  if (icon === "asaas") return <AsaasLogo size={20} />;
  if (icon === "email") return <Email style={{ fontSize: 18, color: active ? "#fff" : "#EA4335" }} />;
  if (icon === "trial") return <AccessTime style={{ fontSize: 18, color: active ? "#fff" : "#FF8C00" }} />;
  if (icon === "login") return <Palette style={{ fontSize: 18, color: active ? "#fff" : "#9C27B0" }} />;
  return null;
};

const GlobalConfig = () => {
  const classes = useStyles();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ loginLogo: false, loginBackground: false });
  const [removing, setRemoving] = useState({ loginLogo: false, loginBackground: false });

  const [config, setConfig] = useState({
    mpAccessToken: "",
    asaasApiKey: "",
    asaasEnvironment: "production",
    smtpHost: "",
    smtpPort: "",
    smtpSecure: "false",
    smtpUser: "",
    smtpPass: "",
    smtpFrom: "",
    trialExpiration: "",
    loginLogo: "",
    loginBackground: "",
    loginWhatsapp: "",
  });

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === "trialExpiration") {
      return setConfig(prev => ({ ...prev, [name]: value.replace(/\D/g, "") }));
    }
    setConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/global-config", config);
      toast.success("Configurações salvas com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/global-config");
      setConfig(prev => ({
        ...prev,
        ...data,
        trialExpiration:
          data.trialExpiration !== undefined && data.trialExpiration !== null
            ? String(data.trialExpiration)
            : prev.trialExpiration,
      }));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleBrandingUpload = async (field, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("field", field);
    try {
      setUploading(prev => ({ ...prev, [field]: true }));
      const { data } = await api.post("/global-config/upload", formData);
      const url = data?.url || data?.[field];
      if (url) setConfig(prev => ({ ...prev, [field]: url }));
      toast.success("Imagem atualizada com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleBrandingRemove = async (field) => {
    try {
      setRemoving(prev => ({ ...prev, [field]: true }));
      await api.post("/global-config/upload/remove", { field });
      setConfig(prev => ({ ...prev, [field]: "" }));
      toast.success("Imagem removida.");
    } catch (err) {
      toastError(err);
    } finally {
      setRemoving(prev => ({ ...prev, [field]: false }));
    }
  };

  if (loading) {
    return (
      <Paper className={classes.root}>
        <div className={classes.loadingWrapper}>
          <CircularProgress />
        </div>
      </Paper>
    );
  }

  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  return (
    <Paper className={classes.root} elevation={0}>
      {/* Page Header */}
      <Box className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>
          Configurações Globais da Plataforma
        </Typography>
        <Typography className={classes.pageSubtitle}>
          Gerencie integrações de pagamento, e-mail, trial e identidade visual
        </Typography>
      </Box>

      {/* Custom Tab Bar */}
      <div className={classes.tabBar}>
        {TABS.map(t => (
          <button
            key={t.id}
            className={`${classes.tabBtn} ${tab === t.id ? classes.tabBtnActive : ""}`}
            onClick={() => setTab(t.id)}
            type="button"
          >
            <TabIconSvg icon={t.icon} active={tab === t.id} />
            {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {/* ── ABA 0: MERCADO PAGO ── */}
        {tab === 0 && (
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <div className={classes.panelLogoBox} style={{ background: "#E8F8FF" }}>
                <MercadoPagoLogo size={36} />
              </div>
              <div>
                <Typography className={classes.panelTitle}>Mercado Pago</Typography>
                <Typography className={classes.panelDescription}>
                  Gateway de pagamento — Pix, cartão e boleto
                </Typography>
              </div>
              {config.mpAccessToken && (
                <Chip
                  icon={<CheckCircle style={{ fontSize: 14 }} />}
                  label="Configurado"
                  size="small"
                  className={classes.statusChip}
                  style={{ background: "#e8f5e9", color: "#2e7d32", marginLeft: "auto" }}
                />
              )}
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Access Token"
                  name="mpAccessToken"
                  value={config.mpAccessToken}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  placeholder="APP_USR-..."
                  type="password"
                />
                <div className={classes.helperText}>
                  Encontre em: <strong>Mercado Pago → Seu negócio → Configurações → Credenciais → Access Token de produção</strong>
                </div>
              </Grid>
            </Grid>

            <div className={classes.infoCard}>
              <Typography className={classes.infoCardText}>
                💡 <strong>Como funciona:</strong> O Mercado Pago processa pagamentos Pix gerados na tela Financeiro.
                Quando o cliente paga, o webhook notifica automaticamente o sistema e a assinatura é renovada.
              </Typography>
            </div>
          </div>
        )}

        {/* ── ABA 1: ASAAS ── */}
        {tab === 1 && (
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <div className={classes.panelLogoBox} style={{ background: "#E8F0FC" }}>
                <AsaasLogo size={36} />
              </div>
              <div>
                <Typography className={classes.panelTitle}>Asaas</Typography>
                <Typography className={classes.panelDescription}>
                  Gateway brasileiro — Pix nativo, boleto e cartão
                </Typography>
              </div>
              {config.asaasApiKey && (
                <Chip
                  icon={<CheckCircle style={{ fontSize: 14 }} />}
                  label="Configurado"
                  size="small"
                  className={classes.statusChip}
                  style={{ background: "#e8f5e9", color: "#2e7d32", marginLeft: "auto" }}
                />
              )}
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="API Key"
                  name="asaasApiKey"
                  value={config.asaasApiKey}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  placeholder="$aact_..."
                  type="password"
                />
                <div className={classes.helperText}>
                  Acesse: <strong>app.asaas.com → Minha Conta → Integrações → Gerar nova chave de API</strong>
                </div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Ambiente"
                  name="asaasEnvironment"
                  value={config.asaasEnvironment || "production"}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  SelectProps={{ native: true }}
                >
                  <option value="production">Produção (cobranças reais)</option>
                  <option value="sandbox">Sandbox (apenas testes)</option>
                </TextField>
                <div className={classes.helperText}>
                  Use Sandbox para testar sem gerar cobranças reais
                </div>
              </Grid>
            </Grid>

            <Divider style={{ margin: "16px 0" }} />

            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
              URL do Webhook Asaas
            </Typography>
            <Typography className={classes.helperText}>
              Configure este endereço no painel Asaas em <strong>Configurações → Webhooks</strong> para receber confirmações de pagamento automaticamente:
            </Typography>
            <div className={classes.webhookBox}>
              {backendUrl}/api/asaas/webhook
            </div>

            <div className={classes.infoCard} style={{ marginTop: 16 }}>
              <Typography className={classes.infoCardText}>
                💡 <strong>Por que Asaas?</strong> Melhor para empresas brasileiras — Pix nativo com QR code, confirmação instantânea,
                boleto bancário e parcelamento no cartão. Taxa menor que Mercado Pago para Pix.
              </Typography>
            </div>
          </div>
        )}

        {/* ── ABA 2: SMTP ── */}
        {tab === 2 && (
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <div className={classes.panelLogoBox} style={{ background: "#FEE8E6" }}>
                <Email style={{ fontSize: 32, color: "#EA4335" }} />
              </div>
              <div>
                <Typography className={classes.panelTitle}>E-mail (SMTP)</Typography>
                <Typography className={classes.panelDescription}>
                  Envio de e-mails automáticos — lembretes, notificações, recuperação
                </Typography>
              </div>
              {config.smtpHost && (
                <Chip
                  icon={<CheckCircle style={{ fontSize: 14 }} />}
                  label="Configurado"
                  size="small"
                  className={classes.statusChip}
                  style={{ background: "#e8f5e9", color: "#2e7d32", marginLeft: "auto" }}
                />
              )}
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Servidor SMTP (Host)"
                  name="smtpHost"
                  value={config.smtpHost}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  placeholder="smtp.hostinger.com"
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  label="Porta"
                  name="smtpPort"
                  value={config.smtpPort}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  placeholder="465"
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  select
                  label="Segurança"
                  name="smtpSecure"
                  value={config.smtpSecure || "false"}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  SelectProps={{ native: true }}
                >
                  <option value="true">SSL/TLS (porta 465)</option>
                  <option value="false">STARTTLS (porta 587)</option>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="E-mail de envio"
                  name="smtpUser"
                  value={config.smtpUser}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  placeholder="suporte@suaempresa.com"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Senha"
                  name="smtpPass"
                  value={config.smtpPass}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  type="password"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Remetente exibido (FROM)"
                  name="smtpFrom"
                  value={config.smtpFrom}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  placeholder="Auzaa Suporte <suporte@suaempresa.com>"
                />
              </Grid>
            </Grid>

            <div className={classes.infoCard}>
              <Typography className={classes.infoCardText}>
                💡 <strong>Hostinger:</strong> Host: smtp.hostinger.com · Porta: 465 · SSL: true.
                Para Gmail: smtp.gmail.com · Porta: 587 · Use uma "Senha de app" (não a senha normal).
              </Typography>
            </div>
          </div>
        )}

        {/* ── ABA 3: TRIAL / ASSINATURA ── */}
        {tab === 3 && (
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <div className={classes.panelLogoBox} style={{ background: "#FFF3E0" }}>
                <AccessTime style={{ fontSize: 32, color: "#FF8C00" }} />
              </div>
              <div>
                <Typography className={classes.panelTitle}>Trial / Assinatura</Typography>
                <Typography className={classes.panelDescription}>
                  Período de teste gratuito para novas empresas cadastradas
                </Typography>
              </div>
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  label="Dias de teste gratuito"
                  name="trialExpiration"
                  value={config.trialExpiration}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                  placeholder="3"
                />
                <div className={classes.helperText}>
                  Cada empresa nova recebe este número de dias grátis antes de precisar pagar.
                  Se vazio, usa o valor do arquivo <code>.env</code> (<code>APP_TRIALEXPIRATION</code>).
                </div>
              </Grid>
            </Grid>

            <div className={classes.infoCard}>
              <Typography className={classes.infoCardText}>
                💡 <strong>Dica para revenda:</strong> Ofereça 7 dias de trial para clínicas e empresas avaliarem
                a plataforma antes de contratar. Após o trial, o sistema bloqueia automaticamente até o pagamento.
              </Typography>
            </div>
          </div>
        )}

        {/* ── ABA 4: LOGIN / CAPA ── */}
        {tab === 4 && (
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <div className={classes.panelLogoBox} style={{ background: "#F3E5F5" }}>
                <Palette style={{ fontSize: 32, color: "#9C27B0" }} />
              </div>
              <div>
                <Typography className={classes.panelTitle}>Login / Identidade Visual</Typography>
                <Typography className={classes.panelDescription}>
                  Personalize a tela de login da plataforma para cada cliente
                </Typography>
              </div>
            </div>

            <Grid container spacing={3}>
              {/* LOGO DO LOGIN */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" style={{ borderRadius: 10 }}>
                  <CardContent>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                      Logo da tela de login
                    </Typography>

                    {config.loginLogo ? (
                      <Box mb={1.5} p={1} style={{ background: "#fafafa", borderRadius: 8, textAlign: "center" }}>
                        <img
                          src={resolveImageUrl(config.loginLogo)}
                          alt="Logo do login"
                          className={classes.brandingPreviewImg}
                        />
                      </Box>
                    ) : (
                      <Box mb={1.5} p={2} style={{ background: "#fafafa", borderRadius: 8, textAlign: "center", border: "1px dashed #ccc" }}>
                        <Typography style={{ fontSize: "0.8rem", color: "#999" }}>Nenhuma logo enviada — usa /logo.png padrão</Typography>
                      </Box>
                    )}

                    <input
                      id="loginLogoUpload"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={e => handleBrandingUpload("loginLogo", e.target.files[0])}
                    />
                    <div className={classes.uploadActions}>
                      <label htmlFor="loginLogoUpload">
                        <Button
                          variant="contained"
                          color="primary"
                          component="span"
                          startIcon={<CloudUpload />}
                          className={classes.uploadButton}
                          disabled={uploading.loginLogo}
                          size="small"
                        >
                          {uploading.loginLogo ? "Enviando..." : "Enviar logo"}
                        </Button>
                      </label>
                      {config.loginLogo && (
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<DeleteOutline />}
                          className={classes.uploadButton}
                          disabled={removing.loginLogo}
                          onClick={() => handleBrandingRemove("loginLogo")}
                          size="small"
                        >
                          {removing.loginLogo ? "Removendo..." : "Remover"}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* CAPA / BACKGROUND */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" style={{ borderRadius: 10 }}>
                  <CardContent>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                      Imagem de fundo (capa do login)
                    </Typography>

                    {config.loginBackground ? (
                      <Box mb={1.5} p={1} style={{ background: "#fafafa", borderRadius: 8, textAlign: "center" }}>
                        <img
                          src={resolveImageUrl(config.loginBackground)}
                          alt="Capa do login"
                          className={classes.brandingPreviewImg}
                        />
                      </Box>
                    ) : (
                      <Box mb={1.5} p={2} style={{ background: "#fafafa", borderRadius: 8, textAlign: "center", border: "1px dashed #ccc" }}>
                        <Typography style={{ fontSize: "0.8rem", color: "#999" }}>Nenhuma capa enviada — usa imagem padrão</Typography>
                      </Box>
                    )}

                    <input
                      id="loginBackgroundUpload"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={e => handleBrandingUpload("loginBackground", e.target.files[0])}
                    />
                    <div className={classes.uploadActions}>
                      <label htmlFor="loginBackgroundUpload">
                        <Button
                          variant="contained"
                          color="primary"
                          component="span"
                          startIcon={<CloudUpload />}
                          className={classes.uploadButton}
                          disabled={uploading.loginBackground}
                          size="small"
                        >
                          {uploading.loginBackground ? "Enviando..." : "Enviar capa"}
                        </Button>
                      </label>
                      {config.loginBackground && (
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<DeleteOutline />}
                          className={classes.uploadButton}
                          disabled={removing.loginBackground}
                          onClick={() => handleBrandingRemove("loginBackground")}
                          size="small"
                        >
                          {removing.loginBackground ? "Removendo..." : "Remover"}
                        </Button>
                      )}
                    </div>
                    <div className={classes.helperText}>
                      Recomendado: <code>.webp</code> ou <code>.jpg</code>, proporção 4:3 ou 16:9
                    </div>
                  </CardContent>
                </Card>
              </Grid>

              {/* WHATSAPP DE SUPORTE */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Link do WhatsApp de suporte"
                  name="loginWhatsapp"
                  value={config.loginWhatsapp}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  placeholder="https://wa.me/5541999999999"
                />
                <div className={classes.helperText}>
                  Exibido no botão "Chamar suporte" na tela de login e na tela de assinatura vencida.
                </div>
              </Grid>
            </Grid>

            <div className={classes.infoCard}>
              <Typography className={classes.infoCardText}>
                💡 <strong>White-label:</strong> Para revenda, cada empresa pode ter sua própria logo e capa na tela de login,
                criando uma experiência personalizada para os usuários delas.
              </Typography>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className={classes.actions}>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            className={classes.saveButton}
            startIcon={!saving && <Save />}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Salvar configurações"}
          </Button>
        </div>
      </form>
    </Paper>
  );
};

export default GlobalConfig;
